import { useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Alert from "@mui/material/Alert";
import Snackbar from "@mui/material/Snackbar";
import { useNavigate } from "react-router-dom";

function decodeModulePath(rawUrl: string): string {
  const trimmed = rawUrl.trim();
  if (!trimmed) return "";

  const modulePathMatcher = /([?&]modulePath=)([^&#]*)/i;
  if (modulePathMatcher.test(trimmed)) {
    return trimmed.replace(
      modulePathMatcher,
      (_match, prefix: string, encodedValue: string) => {
        let decodedValue = encodedValue;
        try {
          decodedValue = decodeURIComponent(encodedValue);
        } catch {
          decodedValue = encodedValue.replace(/%2F/gi, "/");
        }
        return `${prefix}${decodedValue}`;
      },
    );
  }

  return trimmed.replace(/%2F/gi, "/");
}

function buildLocalhostUrl(modulePath: string): string {
  const normalizedPath = modulePath.replace(/^\/+/, "");
  return `http://localhost:5173/${normalizedPath}`;
}

export default function UrlDecodePage() {
  const navigate = useNavigate();
  const [inputUrl, setInputUrl] = useState("");
  const [copiedMessage, setCopiedMessage] = useState("");

  const outputUrl = useMemo(() => decodeModulePath(inputUrl), [inputUrl]);
  const localUrl = useMemo(() => {
    if (!outputUrl) return "";
    try {
      const parsed = new URL(outputUrl);
      const modulePath = parsed.searchParams.get("modulePath");
      if (modulePath) {
        return buildLocalhostUrl(modulePath);
      }
    } catch {
      // Ignore parsing errors and try regex fallback.
    }

    const match = outputUrl.match(/[?&]modulePath=([^&#]*)/i);
    if (!match) return "";
    return buildLocalhostUrl(match[1]);
  }, [outputUrl]);

  const handleCopy = async (text: string, message: string) => {
    if (!text) return;
    await navigator.clipboard.writeText(text);
    setCopiedMessage(message);
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1100, mx: "auto" }}>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
        <Button
          variant="text"
          onClick={() => navigate("/")}
          sx={{ textTransform: "none", alignSelf: "flex-start" }}
        >
          ← Back to Home
        </Button>
      </Stack>

      <Paper variant="outlined" sx={{ p: 3 }}>
        <Typography variant="h5" sx={{ mb: 1 }}>
          URL ModulePath Decoder
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Paste a URL and this page will automatically replace encoded
          <code> %2F </code> with <code> / </code> in <code>modulePath</code>.
          It also builds a localhost URL from <code>modulePath</code>.
        </Typography>

        <Stack spacing={2}>
          <TextField
            label="Input URL"
            value={inputUrl}
            onChange={(e) => setInputUrl(e.target.value)}
            multiline
            minRows={3}
            fullWidth
            placeholder="Paste URL here..."
          />

          <TextField
            label="Decoded URL"
            value={outputUrl}
            multiline
            minRows={3}
            fullWidth
            InputProps={{ readOnly: true }}
          />

          <TextField
            label="Localhost URL"
            value={localUrl}
            multiline
            minRows={3}
            fullWidth
            InputProps={{ readOnly: true }}
          />

          {!inputUrl && (
            <Alert severity="info">
              Paste a URL to start decoding <code>modulePath</code>.
            </Alert>
          )}

          <Stack direction="row" justifyContent="flex-end" spacing={1}>
            <Button
              variant="outlined"
              onClick={() => handleCopy(outputUrl, "Decoded URL copied")}
              disabled={!outputUrl}
            >
              Copy Decoded URL
            </Button>
            <Button
              variant="contained"
              onClick={() => handleCopy(localUrl, "Localhost URL copied")}
              disabled={!localUrl}
            >
              One-Click Copy Localhost URL
            </Button>
          </Stack>
        </Stack>
      </Paper>

      <Snackbar
        open={Boolean(copiedMessage)}
        autoHideDuration={1800}
        onClose={() => setCopiedMessage("")}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="success">
          {copiedMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}
